/**
 * Der eine entsperrte Datei-Spieler. (02.10.2026, Ticket 2919, Sperren/Unterbrechen)
 *
 * ## Der Anlass
 *
 * Martin schaltete den PCM-Weg ein, sperrte mitten in einer Antwort den Bildschirm — und musste danach auf den
 * Play-Knopf der Blase tippen. Journal 10:44 und 10:56: `[sichtbar-d] hidden`, drei Sekunden später
 * `[tts-pcm] datei grund=kontext-interrupted` (iOS unterbricht den AudioContext), dann ein NEUES `<audio>` für den
 * Rest — und ein neues Element startet auf einer versteckten Seite nicht. Es stand bis zum Wachhund
 * (`via=WACHHUND audio=7.63s echt=17.72s`, einmal sogar `audio=6.86s echt=207.99s`: die ganze Zeit des gesperrten
 * Bildschirms plus der Wachhund).
 *
 * ## Was hier steht
 *
 * 1. EIN `<audio>`-Element, das in einer Geste einmal (stumm) gespielt wurde (`entsperreDateiSpieler`, gerufen von
 *    `unlockAudio` und beim Einschalten des Schalters). Safari-Regel: Ein Element, das einmal per Geste gespielt hat,
 *    darf danach ohne Geste neue Quellen spielen. Der Datei-Weg nach einer Unterbrechung nimmt dieses Element
 *    (`nimmDateiSpieler`) statt eines neuen.
 * 2. Der sichere Fallback, wenn auch das nicht anläuft: ein sichtbarer Hinweis „Tippen zum Weiterspielen“
 *    (`voice-v8:tippen-weiterspielen`, app.ts zeigt das Banner) und die ERSTE BERÜHRUNG setzt die Wiedergabe fort
 *    (`beruehrungSetztFort`). Niemand muss mehr den Play-Knopf der Blase suchen.
 *
 * ⚠️ NICHT belegt, nur am iPhone prüfbar: ob WebKit das entsperrte Element in einer versteckten Seite wirklich
 *    starten lässt, ob der Kontext `interrupted` oder `suspended` meldet und ob `resume()` im Hintergrund wirkt.
 *    Das Chromium-Verhalten der Regel steht in tests/browser/datei-spieler-2919.mts.
 */
import { loadSettings } from "./settings.js";
import { gesteDarfWecken } from "./geste-wache.js";
import { GEHOERT_AB_S, startAusAdresse } from "./vorlese-wiederanlauf.js";

/** 1-Sample stille WAV (44 Byte Kopf) als data-URL — dieselbe wie in audio-feedback.ts. */
export const STILLE_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

let spieler: HTMLAudioElement | null = null;
let entsperrt = false;
let hoerer = false;

/** Das eine Element (lazy). `null`, wo es kein `Audio` gibt. */
export function dateiSpieler(): HTMLAudioElement | null {
  if (spieler) return spieler;
  if (typeof Audio === "undefined") return null;
  try {
    spieler = new Audio();
    spieler.preload = "auto";
  } catch {
    spieler = null;
  }
  return spieler;
}

/** Hat das Element schon einmal in einer Geste gespielt? */
export function dateiSpielerEntsperrt(): boolean {
  return entsperrt;
}

/**
 * Aus einer Geste heraus aufrufen: das Element spielt einmal die stille Datei. Legt es nur an, wenn der Schalter
 * an ist (oder gerade eingeschaltet wird) — wer den PCM-Weg nicht nutzt, bekommt kein zusätzliches Element.
 * Probiert bei jeder weiteren Geste erneut, bis es gelungen ist. Wirft nie.
 */
export function entsperreDateiSpieler(schalterGerade = false): void {
  try {
    if (entsperrt) return;
    if (!spieler && !schalterGerade && loadSettings().ttsPcmBeta !== true) return;
    const a = dateiSpieler();
    if (!a) return;
    installiereWiederholung();
    // In der App weckt `play(STILLE)` WebKits Tonsitzung und unterbricht die native Stimme (geste-wache.ts).
    // `entsperrt` bleibt falsch: die nächste Geste nach dem Ende holt es nach.
    if (!gesteDarfWecken("datei-stille")) return;
    // Spielt gerade etwas ernsthaft (kein Entsperr-Versuch mehr nötig)? Dann ist es offenbar entsperrt.
    if (!a.paused && a.src && a.src !== STILLE_WAV) {
      entsperrt = true;
      return;
    }
    a.src = STILLE_WAV;
    a.volume = 0;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(
        () => {
          entsperrt = true;
          try {
            a.pause();
            a.volume = 1;
          } catch {
            /* egal */
          }
        },
        () => {
          /* diese Geste hat es nicht geschafft — die nächste versucht es wieder */
        },
      );
    }
  } catch {
    /* Entsperren ist best-effort */
  }
}

function installiereWiederholung(): void {
  if (hoerer || typeof document === "undefined") return;
  hoerer = true;
  const opts = { capture: true, passive: true } as const;
  for (const ev of ["pointerdown", "keydown", "touchstart"]) {
    try {
      document.addEventListener(ev, () => entsperreDateiSpieler(), opts);
    } catch {
      /* ein fehlender Ereignistyp darf nie stören */
    }
  }
}

/**
 * Das Element für `url` bereitmachen — oder `null`, wenn es nicht entsperrt ist (dann bleibt es beim frischen Element).
 * Es gibt nur EIN Element, weil immer nur EINE Stimme spricht (Ticket 2882): wer beginnt, hat die ältere Schlange
 * vorher abgebrochen und ihr Element pausiert.
 */
export function nimmDateiSpieler(url: string): HTMLAudioElement | null {
  if (!entsperrt) return null;
  const a = dateiSpieler();
  if (!a) return null;
  try {
    a.pause();
    a.volume = 1;
    a.muted = false;
    a.src = url;
    return a;
  } catch {
    return null;
  }
}

/** Nur Tests. */
export function _dateiSpielerZuruecksetzen(): void {
  spieler = null;
  entsperrt = false;
  letzteFortsetzBeruehrung = 0;
  gesteBegonnen = 0;
}

/** Der Hinweis: app.ts zeigt ihn an der Sprech-Pille, am Knopf der Blase und als Banner. */
export const TIPPEN_EREIGNIS = "voice-v8:tippen-weiterspielen";

function meldeTippen(an: boolean): void {
  try {
    window.dispatchEvent(new CustomEvent(TIPPEN_EREIGNIS, { detail: { an } }));
  } catch {
    /* ohne window (Tests) */
  }
}

/** So lange gilt der Klick, der einer fortsetzenden Berührung folgt, als dieselbe Geste. */
export const FORTSETZ_GESTE_MS = 700;
/**
 * So lange darf der Finger liegen bleiben (langes Drücken): Kommt der Klick einer fortsetzenden Berührung erst nach
 * `FORTSETZ_GESTE_MS`, gehört er trotzdem zu ihr. Länger als das ist es kein Tippen mehr — und ein Tipp auf die
 * Pille soll nach einer Berührung OHNE Klick (Wischen) nicht lange ins Leere gehen.
 */
export const GESTE_HOECHSTENS_MS = 2000;
let letzteFortsetzBeruehrung = 0;
/** Eine fortsetzende Berührung hat begonnen (Finger unten), ihr Klick steht noch aus. */
let gesteBegonnen = 0;
let klickHoerer = false;

/**
 * Eine Berührung hat eben fortgesetzt: merken — und ihren Klick erkennen, auch wenn er spät kommt. Der Hörer hängt
 * einmal am Dokument (Einfang-Phase, also vor den Knöpfen) und tut nichts, solange keine solche Berührung offen ist.
 */
function merkeFortsetzGeste(art: string): void {
  letzteFortsetzBeruehrung = Date.now();
  if (art === "click") {
    gesteBegonnen = 0;
    return;
  }
  gesteBegonnen = Date.now();
  if (klickHoerer || typeof document === "undefined") return;
  klickHoerer = true;
  document.addEventListener(
    "click",
    () => {
      if (gesteBegonnen && Date.now() - gesteBegonnen < GESTE_HOECHSTENS_MS) letzteFortsetzBeruehrung = Date.now();
      gesteBegonnen = 0;
    },
    true,
  );
}

/**
 * Hat eben eine Berührung die Wiedergabe fortgesetzt? (05.10.2026)
 *
 * Die Berührung, die fortsetzt, kommt als `pointerdown`/`touchend`; der `click` derselben Geste folgt Bruchteile
 * einer Sekunde später — auf der Sprech-Pille hieße er „stoppen“, am Knopf der Blase „pausieren“: Die Wiedergabe
 * liefe an und bräche sofort wieder ab. Beide Knöpfe fragen darum hier und lassen diesen einen Klick aus.
 * Bleibt der Finger länger liegen (bis `GESTE_HOECHSTENS_MS`), erneuert der Klick selbst die Marke (`merkeFortsetzGeste`).
 */
export function fortsetzBeruehrungGerade(): boolean {
  if (letzteFortsetzBeruehrung <= 0) return false;
  const seit = Date.now() - letzteFortsetzBeruehrung;
  return seit >= 0 && seit < FORTSETZ_GESTE_MS;
}

/**
 * Das Stück wartet auf eine Berührung (der Browser hat `play()` abgelehnt, oder es stand nach mehreren frischen
 * Starts immer noch): sichtbarer Hinweis, und die ERSTE Berührung — oder die Rückkehr in den Vordergrund — spielt
 * `audio` weiter. Gibt die Aufräum-Funktion zurück (nimmt Hinweis und Hörer zurück). `beiLauf` meldet, wenn es
 * wirklich läuft.
 *
 * ⚠️ 05.10.2026 — „läuft“ heißt: die ZEIT rückt vor, um mindestens eine halbe Sekunde (`GEHOERT_AB_S`). Bis dahin
 *    nahm `playing` den Hinweis zurück; das Ereignis kommt aber schon, wenn Daten da sind und das Element nicht
 *    pausiert ist. Journal 10:55: Hinweis um 10:55:30,7, um 10:55:32,0 kam die Datei an, `playing` feuerte, der
 *    Hinweis ging weg — und das Stück (4,5 s) endete in den folgenden 11,5 s nicht.
 * ⚠️ Ein Element, das „spielt“ meldet und steht, hört auf ein weiteres `play()` nicht (es spielt ja schon). Darum
 *    hält die Berührung es erst an und startet es dann — in der Geste.
 * ⚠️ `beiGeste` (für ein Stück, das nach mehreren frischen Starts immer noch steht): dort hilft auch Anhalten und
 *    Starten nicht sicher — am iPhone half seit Ticket 2547 nur ein frischer Spieler. Die Berührung geht dann an den
 *    Aufrufer (tts-queue.ts gibt dem Stück einen frischen Spieler), EINMAL je Berührung; dieses Element bleibt, wie es ist.
 */
export function beruehrungSetztFort(audio: HTMLMediaElement, beiLauf?: () => void, beiGeste?: () => void): () => void {
  let fertig = false;
  const basis = Math.max(audio.currentTime || 0, startAusAdresse(audio.currentSrc || audio.src || ""));
  /**
   * Wann die laufende Berührung ihren Versuch bekam (0 = noch keinen, oder er wurde abgelehnt). Eine Berührung kommt
   * als mehrere Ereignisse (pointerdown, touchend, click) — sie bekommt EINEN angenommenen Start, sonst hielte das
   * zweite Ereignis an, was das erste eben gestartet hat. Versuche OHNE Berührung (beim Zeigen des Hinweises, bei
   * der Rückkehr) zählen hier nicht: eine Berührung darf danach immer.
   */
  let gesteVersuch = 0;
  const probiere = (ausGeste: boolean): void => {
    if (fertig) return;
    if (ausGeste && gesteVersuch && Date.now() - gesteVersuch < FORTSETZ_GESTE_MS) return;
    try {
      if (!audio.paused) audio.pause();
      if (ausGeste) gesteVersuch = Date.now();
      const p = audio.play();
      // Abgelehnt (auf iOS zählt erst `touchend` als Geste, nicht `pointerdown`): das nächste Ereignis versucht es wieder.
      if (p && typeof p.then === "function") {
        p.catch(() => {
          if (ausGeste) gesteVersuch = 0;
        });
      }
    } catch {
      if (ausGeste) gesteVersuch = 0;
    }
  };
  const nachBeruehrung = (ev: Event): void => {
    if (fertig) return;
    merkeFortsetzGeste(ev.type);
    if (!beiGeste) {
      probiere(true);
      return;
    }
    // Dieselbe Berührung kommt als mehrere Ereignisse — der Aufrufer bekommt sie einmal.
    if (gesteVersuch && Date.now() - gesteVersuch < FORTSETZ_GESTE_MS) return;
    gesteVersuch = Date.now();
    beiGeste();
  };
  const nachRueckkehr = (): void => {
    if (document.visibilityState === "visible") probiere(false);
  };
  const laeuft = (): void => {
    // Erst eine halbe Sekunde Fortgang gilt als „es läuft“: hält das System das Element gleich nach dem Start wieder
    // an (60 ms Ton, gemessen im WebKit-Test), ginge der Hinweis sonst weg und käme drei Sekunden später wieder.
    if (fertig || !(audio.currentTime > basis + GEHOERT_AB_S)) return;
    aufraeumen();
    beiLauf?.();
  };
  // `click` für Vorleseprogramme und Schalter-Steuerung: die lösen nur einen Klick aus, keine Berührung.
  const ereignisse = ["pointerdown", "touchend", "keydown", "click"];
  function aufraeumen(): void {
    if (fertig) return;
    fertig = true;
    for (const ev of ereignisse) document.removeEventListener(ev, nachBeruehrung, true);
    document.removeEventListener("visibilitychange", nachRueckkehr);
    audio.removeEventListener("timeupdate", laeuft);
    meldeTippen(false);
  }
  for (const ev of ereignisse) document.addEventListener(ev, nachBeruehrung, true);
  document.addEventListener("visibilitychange", nachRueckkehr);
  audio.addEventListener("timeupdate", laeuft);
  meldeTippen(true);
  // Ist die Seite schon wieder vorn, gleich versuchen — ohne auf die Berührung zu warten.
  if (document.visibilityState === "visible") probiere(false);
  return aufraeumen;
}
